import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Sparkles } from 'lucide-react';
import { playSfx } from './loungeMeta';

/**
 * Shared Lounge helpers: level system + confetti celebration.
 *
 * getLevel(game): returns the highest unlocked level for a game (localStorage).
 * setLevel(game, n): persists a newly-reached level.
 * getHighScore / setHighScore: same for high scores.
 *
 * Confetti / Congrats — a compact particle burst rendered inside the game
 * card overlay. Uses framer-motion so it feels premium without new deps.
 */
export const MAX_LEVEL = 20;

const key = (game, kind) => `lounge_${game}_${kind}`;

export function getLevel(game) { return clamp(Number(localStorage.getItem(key(game, 'level')) || 1), 1, MAX_LEVEL); }
export function setLevel(game, n) {
  const cur = getLevel(game);
  const nx = clamp(Math.max(cur, n), 1, MAX_LEVEL);
  localStorage.setItem(key(game, 'level'), String(nx));
  return nx;
}
export function getHighScore(game) { return Number(localStorage.getItem(key(game, 'high')) || 0); }
export function setHighScore(game, s) {
  const cur = getHighScore(game);
  const nx = Math.max(cur, Math.floor(s));
  localStorage.setItem(key(game, 'high'), String(nx));
  return nx;
}

/** Level 1 → 20 progressive scaling (0 easiest, 1 hardest). */
export function levelT(level) { return (clamp(level, 1, MAX_LEVEL) - 1) / (MAX_LEVEL - 1); }
export function lerp(a, b, t) { return a + (b - a) * t; }
function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

/** Per-game target for a level. */
export function levelTarget(game, level) {
  const lv = Math.max(1, Math.min(MAX_LEVEL, level));
  if (game === 'flyer') return 4 + lv;      // level 1 = 5 pipes, level 20 = 24
  if (game === 'drift') return 4 + lv;      // level 1 = 5 tokens, level 20 = 24
  return lv;
}

/** Levels don't rely on a countdown anymore. Kept for backwards-compat. */
export const LEVEL_DURATION = 30;

/** ------------------------- Congrats + Confetti ------------------------- */

export function Congrats({ open, title, subtitle, onPlayAgain, onNext, canAdvance, testid = 'congrats' }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 z-30 flex flex-col items-center justify-center text-center px-4"
          style={{ background: 'rgba(11,25,44,0.86)', backdropFilter: 'blur(8px)' }}
          data-testid={testid}
        >
          <Confetti/>
          <motion.div
            initial={{ scale: 0.85, y: 16 }} animate={{ scale: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 240, damping: 22 }}
            className="relative"
          >
            <Trophy size={44} className="text-gold mx-auto" strokeWidth={1.5}/>
            <p className="text-[11px] uppercase tracking-[0.28em] text-gold font-semibold mt-3">Congrats</p>
            <p className="font-heading text-3xl lg:text-4xl text-white mt-1">{title}</p>
            {subtitle && <p className="text-sm text-white/70 mt-2 max-w-xs mx-auto leading-relaxed">{subtitle}</p>}
            <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
              {canAdvance && onNext && (
                <button onClick={onNext} data-testid={`${testid}-next`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-navy font-semibold hover:brightness-95 text-sm">
                  <Sparkles size={13}/> Next level
                </button>
              )}
              {onPlayAgain && (
                <button onClick={onPlayAgain} data-testid={`${testid}-again`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/25 text-white font-semibold text-sm hover:bg-white/15">
                  Play again
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const CONFETTI_COLORS = ['#D4AF37', '#FFD76A', '#F5F3EF', '#E1C88A', '#FFFFFF'];

function Confetti() {
  const [pieces] = useState(() => Array.from({ length: 42 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    dx: (Math.random() - 0.5) * 60,
    dy: 60 + Math.random() * 40,
    rot: Math.random() * 360,
    dr: (Math.random() - 0.5) * 720,
    delay: Math.random() * 0.35,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    size: 6 + Math.random() * 6,
  })));
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          initial={{ x: `${p.x}vw`, y: '-8vh', opacity: 0, rotate: p.rot }}
          animate={{ x: `${p.x + p.dx}vw`, y: `${p.dy}vh`, opacity: [0, 1, 1, 0], rotate: p.rot + p.dr }}
          transition={{ duration: 2.2, delay: p.delay, ease: [0.2, 0.7, 0.3, 1] }}
          style={{
            position: 'absolute',
            top: 0, left: 0,
            width: p.size, height: p.size * 0.5,
            background: p.color,
            borderRadius: 2,
            boxShadow: `0 0 10px ${p.color}77`,
          }}
        />
      ))}
    </div>
  );
}

/** Countdown timer helper — returns [remaining, expired] and calls onExpire once when the clock hits 0. */
export function useCountdown(running, seconds, onExpire) {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => { setRemaining(seconds); }, [seconds]);
  useEffect(() => {
    if (!running) return;
    const start = performance.now();
    const startVal = remaining;
    let raf = 0;
    let fired = false;
    const tick = (ts) => {
      const el = (ts - start) / 1000;
      const r = Math.max(0, startVal - el);
      setRemaining(r);
      if (r <= 0 && !fired) { fired = true; if (onExpire) onExpire(); return; }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps
  return remaining;
}

/* ----------------- Pre-game 3-2-1-GO countdown ----------------- */

/**
 * useLoungeCountdown — small hook every Lounge game plugs in.
 *
 * Usage:
 *   const { countdown, beginCountdown, cancelCountdown } = useLoungeCountdown();
 *   const start = () => beginCountdown(() => actuallyStartTheGame());
 *
 * Then render <LoungeCountdown n={countdown} /> inside your canvas wrapper.
 *
 * Ticks at ~1s per number (3 → 2 → 1 → GO). Plays the `countdown` SFX on each
 * tick (silently no-ops if the file is missing).
 */
export function useLoungeCountdown() {
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  const cancelCountdown = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setCountdown(0);
  };

  const beginCountdown = (onFire) => {
    cancelCountdown();
    const sequence = [3, 2, 1, 'GO'];
    let i = 0;
    const step = () => {
      const val = sequence[i];
      setCountdown(val);
      playSfx('countdown', { volume: 0.35 });
      i += 1;
      if (i < sequence.length) {
        // Slightly shorter tail on "GO" so play kicks off snappy
        timerRef.current = setTimeout(step, val === 1 ? 700 : 900);
      } else {
        // After "GO", brief pause then fire
        timerRef.current = setTimeout(() => {
          setCountdown(0);
          timerRef.current = null;
          if (onFire) onFire();
        }, 350);
      }
    };
    step();
  };

  // Cleanup on unmount
  useEffect(() => () => cancelCountdown(), []);

  return { countdown, beginCountdown, cancelCountdown };
}

/**
 * LoungeCountdown — full-canvas dim + big pulsing 3/2/1/GO number.
 * Renders only when `n` is truthy. Absolutely positioned inside a relative parent.
 */
export function LoungeCountdown({ n, color = '#7dfaff', testid = 'lounge-countdown' }) {
  return (
    <AnimatePresence>
      {n ? (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, rgba(11,25,44,0.55) 0%, rgba(11,25,44,0.35) 100%)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
          }}
          data-testid={testid}
        >
          <motion.div
            key={String(n)}
            initial={{ scale: 0.35, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 1.6, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 280, damping: 18 }}
            className="font-heading tabular-nums select-none"
            style={{
              fontSize: n === 'GO' ? 'clamp(56px, 12vw, 120px)' : 'clamp(96px, 20vw, 220px)',
              color,
              textShadow: `0 0 24px ${color}, 0 0 60px ${color}aa, 0 0 100px ${color}55`,
              fontWeight: 800,
              letterSpacing: n === 'GO' ? '0.06em' : '0',
              lineHeight: 1,
            }}
            data-testid={`${testid}-${n}`}
          >
            {n}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
